import { MapObjectProperties } from "@iventis/map-types";
import GeoJSON from "geojson";
import { Content } from "@iventis/translations/content/typed-content";
import { Body2 } from "@iventis/styles/src/components/texts";
import React, { FunctionComponent, useState, useMemo } from "react";
import { styled } from "@iventis/styles";
import { canParseInt, thousandSeparator } from "@iventis/utilities";
import { getLengthInAppropriateUnits } from "@iventis/map-engine/src/utilities/geojson-helpers";
import length from "@turf/length";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { NumberOnlyInput } from "@iventis/components/src/number-only-input";
import { contentCss, inputCss } from "./analysis.helpers";
import { calcFlowRate, FruinServiceLevelsComponent } from "./fruin-service-levels";

export const AnalysisFlowRateComponent: FunctionComponent<{ feature: GeoJSON.Feature<GeoJSON.LineString, MapObjectProperties>; unitOfMeasurement: UnitOfMeasurement }> = ({
    feature,
    unitOfMeasurement,
}) => {
    const translate = useIventisTranslate();
    // People per metre per minute
    const [ppmmInputValue, setPpmmInputValue] = useState<string>("80");
    const flowRate = useMemo(() => {
        if (canParseInt(ppmmInputValue)) {
            const perMinute = calcFlowRate(length(feature, { units: "meters" }), parseInt(ppmmInputValue, 10));
            return { perMinute: thousandSeparator(perMinute), perHour: thousandSeparator(perMinute * 60) };
        }
        return { perMinute: 0, perHour: 0 };
    }, [ppmmInputValue, feature]);

    return (
        <>
            <StyledContent>
                <Body2 className="bold">{translate(Content.map2.analysis.flow_rate_content.total_width)}</Body2>
                <Body2>{getLengthInAppropriateUnits(feature, unitOfMeasurement)}</Body2>
            </StyledContent>
            <StyledContent>
                <Body2 className="bold">{translate(Content.map2.analysis.flow_rate_title)}</Body2>
                <Body2>{translate(Content.map2.analysis.flow_rate_content.per_minute_per_hour, { perMinute: flowRate.perMinute, perHour: flowRate.perHour })}</Body2>
                <div>
                    <Body2>{translate(Content.map2.analysis.flow_rate_content.at)}</Body2>
                    <NumberOnlyInput value={ppmmInputValue} onChange={(e) => setPpmmInputValue(e.target.value)} style={inputCss} type="number" variant="outlined" />
                    <Body2>{translate(Content.map2.analysis.flow_rate_content.people_meter_minute)}</Body2>
                </div>
            </StyledContent>
            <StyledContent data-testid="flow-rate-analysis-result">
                <Body2 className="bold">{translate(Content.map2.analysis.flow_rate_content.fruin_service_levels)}</Body2>
                <FruinServiceLevelsComponent fruinType="flowRate" magnitude={length(feature, { units: "meters" })} />
            </StyledContent>
        </>
    );
};

const StyledContent = styled.div`
    ${contentCss}
`;
